import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { graphSettingsRouteConfig } from 'src/app/graph/routes';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { UserPermission } from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { UserRolesSlider } from '../../../../explorerPage/operationCollections/operationCollectionsModals/UserRolesSlider';

const validationSchema = Yup.object().shape({
  create: Yup.mixed<GraphQLTypes.UserPermission>()
    .oneOf(Object.values(GraphQLTypes.UserPermission))
    .notOneOf([GraphQLTypes.UserPermission.CONSUMER]),
  edit: Yup.mixed<GraphQLTypes.UserPermission>()
    .oneOf(Object.values(GraphQLTypes.UserPermission))
    .notOneOf([GraphQLTypes.UserPermission.CONSUMER]),
});

type FormValues = Yup.InferType<typeof validationSchema>;

export const ProposalPermissionsModal = ({
  isOpen,
  onClose,
  minProposalRoles,
}: {
  isOpen: boolean;
  onClose: () => void;
  minProposalRoles: GraphQLTypes.ApprovalsAndPermissionsCardFragment_minProposalRoles;
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const { graphId } = useRouteParams(graphSettingsRouteConfig);

  const [updateMinPermissions] = useMutation<
    GraphQLTypes.ProposalPermissionsModalUpdatePermissionsMutation,
    GraphQLTypes.ProposalPermissionsModalUpdatePermissionsMutationVariables
  >(gql`
    mutation ProposalPermissionsModalUpdatePermissionsMutation(
      $graphId: ID!
      $input: SetProposalRolesInput!
    ) {
      service(id: $graphId) {
        setMinProposalRoles(input: $input) {
          ... on Service {
            id
            minProposalRoles {
              create
            }
          }
          ... on PermissionError {
            message
          }
          ... on ValidationError {
            message
          }
        }
      }
    }
  `);

  const {
    values,
    touched,
    isSubmitting,
    isValid,
    setSubmitting,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    resetForm,
  } = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: minProposalRoles,
    onSubmit: async ({ create, edit }) => {
      setSubmitting(true);
      const { data: responseData } = await updateMinPermissions({
        variables: {
          graphId,
          input: {
            create,
            edit,
          },
        },
        update: (cache) => {
          cache.evict({
            id: cache.identify({
              __typename: 'Service',
              id: graphId,
            }),
            fieldName: 'minProposalRoles',
          });
        },
      });
      if (responseData?.service?.setMinProposalRoles.__typename === 'Service') {
        onClose();
        resetForm({});
        setSubmitting(false);
        return;
      }
      setSubmitting(false);

      if (
        responseData?.service?.setMinProposalRoles.__typename ===
          'PermissionError' ||
        responseData?.service?.setMinProposalRoles.__typename ===
          'ValidationError' ||
        !responseData?.service?.setMinProposalRoles
      ) {
        setErrorMessage(
          responseData?.service?.setMinProposalRoles.message ??
            'An error has occurred',
        );
      } else {
        setErrorMessage(
          assertUnreachableOrReturnDefault(
            responseData.service.setMinProposalRoles,
            'An error has occurred',
          ),
        );
      }
    },
    validationSchema,
  });

  const formIsTouched = useMemo(
    () => isMinLengthArray(1, Object.values(touched)),
    [touched],
  );

  const handleClose = useCallback(() => {
    onClose();
    resetForm({});
  }, [onClose, resetForm]);

  return (
    <Modal size="xl" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Proposal permissions</ModalTitle>
          <ModalDescription>
            These permissions can be modified at any time.
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          <FormControl>
            <FormLabel>Create a proposal</FormLabel>
            <div className="rounded border border-primary px-12 pb-8 pt-4">
              <UserRolesSlider
                minEditRole={values.create}
                setMinEditRole={(value) => {
                  setFieldValue('create', value ?? UserPermission.GRAPH_ADMIN);
                  setFieldTouched('create');
                }}
                excludedRoles={[GraphQLTypes.UserPermission.CONSUMER]}
                label="Create and edit a proposal"
              />
            </div>
          </FormControl>
          <FormControl>
            <FormLabel>Edit a proposal</FormLabel>
            <div className="rounded border border-primary px-12 pb-8 pt-4">
              <UserRolesSlider
                minEditRole={values.edit}
                setMinEditRole={(value) => {
                  setFieldValue('edit', value ?? UserPermission.GRAPH_ADMIN);
                  setFieldTouched('edit');
                }}
                excludedRoles={[GraphQLTypes.UserPermission.CONSUMER]}
                label="Edit a proposal"
              />
            </div>
            <FormHelperText>
              Authors can always edit their own proposals, regardless of their
              role.
            </FormHelperText>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={!isValid || !formIsTouched}
              isLoading={isSubmitting}
              loadingText="Save"
              onClick={() => handleSubmit()}
            >
              Save
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
